import api from './api';
import {
  CreateDadosFinanceirosDto,
  FilterSearchDadosFinanceirosDto,
  FilterSearchDadosFinanceirosForReportDto,
  GetDadosFinanceirosDto,
  ReajusteValoresFinanceirosDto,
  RelatorioComparacaoValores,
} from '../shared/dtos/DadosFinanceiros.dto';
import { GetFinancialRecordDto } from '../shared/dtos/FichaFinanceira.dto';
import { formatarDataParaAPI } from '../utils/dateUtils/dateUtils';
import { ResultDto } from '../shared/dtos/Result.dto';

interface DependenteAtivo {
  id: number;
  nome: string;
  dataExclusao: string | null;
  status: string;
  movimentacoes?: Movimentacao[];
}

interface Movimentacao {
  id: number;
  tipo: 'INCLUSAO' | 'EXCLUSAO' | 'REATIVACAO';
  data: string;
  dependenteId?: number;
}

class DadosFinanceirosService {
  async createDadosFinanceiros(
    dadosFinanceirosData: CreateDadosFinanceirosDto,
    idContribuinte: number
  ): Promise<any> {

    // Garantir que as datas estejam no formato correto
    const formattedData = {
      ...dadosFinanceirosData,
      dataInclusao: dadosFinanceirosData.dataInclusao
        ? formatarDataParaAPI(dadosFinanceirosData.dataInclusao)
        : null,
      dataExclusao: dadosFinanceirosData.dataExclusao
        ? formatarDataParaAPI(dadosFinanceirosData.dataExclusao)
        : null,
      valorTitular: dadosFinanceirosData.valorTitular
        ? Number(dadosFinanceirosData.valorTitular.replace(',', '.').replace(/\.(?=\d{3}(\.|\s|$))/g, '')) >= 1000
          ? dadosFinanceirosData.valorTitular.replace(',', '.').replace(/\.(?=\d{3}(\.|\s|$))/g, '')
          : dadosFinanceirosData.valorTitular.replace(',', '.')
        : '0.00',
      valorDependente: dadosFinanceirosData.valorDependente
        ? Number(dadosFinanceirosData.valorDependente.replace(',', '.').replace(/\.(?=\d{3}(\.|\s|$))/g, '')) >= 1000
          ? dadosFinanceirosData.valorDependente.replace(',', '.').replace(/\.(?=\d{3}(\.|\s|$))/g, '')
          : dadosFinanceirosData.valorDependente.replace(',', '.')
        : '0.00',
    };

    const response = await api.post(
      `/dados-financeiros/cadastrar/${idContribuinte}`,
      formattedData
    );
    return response.data;
  }

  async getDadosFinanceirosByContribuinte(
    idContribuinte: number
  ): Promise<GetDadosFinanceirosDto[]> {
    try {
      const response = await api.get<GetDadosFinanceirosDto[]>(
        `/dados-financeiros/listar/${idContribuinte}`
      );
      const dadosFinanceiros = response.data.map((dado) => ({
        ...dado,
        dataInclusao: dado.dataInclusao ? dado.dataInclusao.split('T')[0] : '',
        dataExclusao: dado.dataExclusao ? dado.dataExclusao.split('T')[0] : null,
      }));
      return dadosFinanceiros;
    } catch (error) {
      console.error('Erro detalhado:', error);
      throw error;
    }
  }

  async getBySearchParams(
    data: FilterSearchDadosFinanceirosDto,
  ): Promise<GetDadosFinanceirosDto[]> {

    try {
      // Enviando os parâmetros para o backend
      const response = await api.post(
        `dados-financeiros/get-by-search-params`,
        data,
        {
          timeout: 0,
        }
      );

      return response.data
    } catch (error: any) {
      // Tratamento de erro melhorado
      console.error("Erro ao buscar os dados financeiros:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Retorna um objeto vazio em vez de lançar o erro
      return []
    }
  }

  async gerarRelatorioComparacaoValores(
    data: FilterSearchDadosFinanceirosForReportDto,
  ): Promise<RelatorioComparacaoValores[]> {

    try {
      // Enviando os parâmetros para o backend
      const response = await api.post(
        `dados-financeiros/relatorio-comparacao-valores`,
        data,
        {
          timeout: 0,
        }
      );

      return response.data
    } catch (error: any) {
      // Tratamento de erro melhorado
      console.error("Erro ao buscar os dados financeiros:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Retorna um objeto vazio em vez de lançar o erro
      return []
    }
  }


  async reajusteValoresFinanceiros(
    data: ReajusteValoresFinanceirosDto,
  ): Promise<ResultDto> {

    try {
      // Enviando os parâmetros para o backend
      const response = await api.post(
        `dados-financeiros/reajuste-valores-financeiros`,
        data,
        {
          timeout: 0,
        }
      );

      return response.data
    } catch (error: any) {
      // Tratamento de erro melhorado
      console.error("Erro ao buscar os dados financeiros:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Retorna um objeto vazio em vez de lançar o erro
      return {
        success: false,
        message: error.message,
        data: null,
      }
    }
  }

  async updateDadosFinanceiros(
    id: number,
    dadosFinanceirosData: Partial<CreateDadosFinanceirosDto>
  ): Promise<any> {

    try {
      // Garantir que as datas estejam no formato correto
      const formattedData = {
        ...dadosFinanceirosData,
        dataInclusao: dadosFinanceirosData.dataInclusao
          ? formatarDataParaAPI(dadosFinanceirosData.dataInclusao)
          : null,
        dataExclusao: dadosFinanceirosData.dataExclusao
          ? formatarDataParaAPI(dadosFinanceirosData.dataExclusao)
          : null,
        valorTitular: dadosFinanceirosData.valorTitular
          ? Number(dadosFinanceirosData.valorTitular.replace(',', '.').replace(/\.(?=\d{3}(\.|\s|$))/g, '')) >= 1000
            ? dadosFinanceirosData.valorTitular.replace(',', '.').replace(/\.(?=\d{3}(\.|\s|$))/g, '')
            : dadosFinanceirosData.valorTitular.replace(',', '.')
          : '0.00',
        valorDependente: dadosFinanceirosData.valorDependente
          ? Number(dadosFinanceirosData.valorDependente.replace(',', '.').replace(/\.(?=\d{3}(\.|\s|$))/g, '')) >= 1000
            ? dadosFinanceirosData.valorDependente.replace(',', '.').replace(/\.(?=\d{3}(\.|\s|$))/g, '')
            : dadosFinanceirosData.valorDependente.replace(',', '.')
          : '0.00',
        frequenciaParcela: Number(dadosFinanceirosData.frequenciaParcela || 1),
        acumulaPagamento: Number(dadosFinanceirosData.acumulaPagamento || 0),
      };


      // Usando o mesmo padrão dos outros serviços (/entidade/update/id)
      const response = await api.patch(`/dados-financeiros/update/${id}`, formattedData);
      return response.data;
    } catch (error: any) {
      console.error('Erro na atualização:', error.response?.data || error);
      throw error;
    }
  }

  async reativarParcelas(ids: number[]): Promise<any> {
    try {
      const response = await api.post('/ficha-financeira/reactivate', {
        installmentsId: ids,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao reativar parcelas:', error);
      throw error;
    }
  }

  async searchParcela(idContribuinte: number): Promise<GetFinancialRecordDto[]> {
    try {
      const response = await api.get(
        `ficha-financeira/findAllByContribuinte/${idContribuinte}`
      );
      return sortParcelas(response.data as GetFinancialRecordDto[]);
    } catch (error) {
      console.error('Erro ao buscar parcelas:', error);
      return [];
    }
  }

  async searchParcelaByData(params: {
    nossoNumero?: number;
    valorParcela?: number;
  }): Promise<GetFinancialRecordDto[]> {
    try {
      const response = await api.get('ficha-financeira/parcela-buscada', { params });
      return response.data as GetFinancialRecordDto[];
    } catch (error) {
      console.error('Erro ao buscar parcelas por dados:', error);
      return [];
    }
  }

  async filterParcela(
    idContribuinte: number,
    params: any
  ): Promise<GetFinancialRecordDto[]> {
    try {
      const response = await api.post(
        `ficha-financeira/filtro-aplicado/${idContribuinte}`,
        params
      );
      return response.data as GetFinancialRecordDto[];
    } catch (error) {
      console.error('Erro ao filtrar parcelas:', error);
      return [];
    }
  }

  async manualClearance(
    installments: { id: number; installmentValue: number }[],
    paidValue: number,
    totalValue: number
  ): Promise<any> {
    try {
      // Garante que todos os valores são números com 2 casas decimais
      const formattedInstallments = installments.map((item) => ({
        id: Number(item.id),
        installmentValue: Number(Number(item.installmentValue).toFixed(2)),
      }));

      const payload = {
        installments: formattedInstallments,
        paidValue: Number(Number(paidValue).toFixed(2)),
        totalValue: Number(Number(totalValue).toFixed(2)),
        dataPagamento: new Date().toISOString().split('.')[0], // Remove os milissegundos
      };


      const response = await api.post('/ficha-financeira/baixa-manual', payload);

      if (!response.data) {
        throw new Error('Resposta vazia do servidor');
      }

      return response.data;
    } catch (error: any) {
      console.error('Erro ao realizar baixa manual:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        payload: error.config?.data,
      });
      throw error;
    }
  }

  async getManualPayments(): Promise<GetFinancialRecordDto[]> {
    try {
      const response = await api.get<GetFinancialRecordDto[]>(
        'ficha-financeira/manual-payments'
      );

      if (!response.data) {
        throw new Error('Resposta vazia do servidor');
      }

      return sortParcelas(response.data);
    } catch (error: any) {
      console.error('Erro ao buscar pagamentos manuais:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      // Tenta extrair a mensagem mais específica do erro
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Não foi possível carregar os pagamentos manuais.';

      throw new Error(errorMessage);
    }
  }

  async getManualPaymentsByDate(
    startDate?: string,
    endDate?: string
  ): Promise<GetFinancialRecordDto[]> {
    try {
      const params = {
        startDate,
        endDate,
      };

      const response = await api.get('ficha-financeira/manual-payments', { params });
      return sortParcelas(response.data as GetFinancialRecordDto[]);
    } catch (error) {
      console.error('Erro ao buscar pagamentos manuais:', error);
      return [];
    }
  }

  async getMovimentacoes(idContribuinte: number): Promise<Movimentacao[]> {
    try {
      const response = await api.get<Movimentacao[]>(`/movimentacoes/${idContribuinte}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar movimentações:', error);
      return [];
    }
  }

  async getDependentesAtivos(idContribuinte: number): Promise<DependenteAtivo[]> {
    try {
      // Busca dependentes e movimentações
      const [dependentes, movimentacoes] = await Promise.all([
        api.get<DependenteAtivo[]>(`/contribuintes/${idContribuinte}/dependentes`),
        api.get<Movimentacao[]>(`/contribuintes/${idContribuinte}/movimentacoes`)
      ]);

      // Filtra apenas dependentes ativos ou reativados
      const dependentesAtivos = dependentes.data.filter(dependente => {
        // Pega todas as movimentações deste dependente
        const movsDoDepentente = movimentacoes.data.filter(mov => mov.dependenteId === dependente.id);

        if (movsDoDepentente.length === 0) {
          return false;
        }

        // Ordena por data, mais recente primeiro
        const movimentacoesOrdenadas = movsDoDepentente.sort(
          (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
        );

        // Pega a última movimentação
        const ultimaMovimentacao = movimentacoesOrdenadas[0];
        const estaAtivo = ['INCLUSAO', 'REATIVACAO'].includes(ultimaMovimentacao.tipo);

        return estaAtivo;
      });

      return dependentesAtivos;
    } catch (error) {
      console.error('Erro ao buscar dependentes ativos:', error);
      throw error;
    }
  }
}

// Função auxiliar para ordenar parcelas
export const sortParcelas = (
  parcelas: GetFinancialRecordDto[]
): GetFinancialRecordDto[] => {
  // Define a ordem de prioridade dos status
  const statusPrioridade: { [key: string]: number } = {
    Ativa: 1,
    Cancelada: 2,
    Paga: 3,
    // Vencida: 4,
  };

  return parcelas.sort((a, b) => {
    // Primeiro critério: status da parcela
    const statusA = statusPrioridade[a.statusParcela] || 999;
    const statusB = statusPrioridade[b.statusParcela] || 999;

    if (statusA !== statusB) {
      return statusA - statusB;
    }

    // Segundo critério: data de vencimento
    return new Date(a.dataVencimento).getTime() - new Date(b.dataVencimento).getTime();
  });
};

const dadosFinanceirosService = new DadosFinanceirosService();
export default dadosFinanceirosService;
